import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { clearTokens, getRefreshToken } from "@/lib/token-manager";
import * as adminAuthService from "../services/admin-auth.service";

interface AdminAuthState {
	operator: adminAuthService.AdminBootstrap | null;
	isLoading: boolean;
	isBootstrapping: boolean;
	error: string | null;
	otpSent: boolean;
	pendingPhone: string | null;
	sendOtp: (phone: string) => Promise<void>;
	verifyOtp: (phone: string, otp: string) => Promise<void>;
	bootstrap: () => Promise<void>;
	logout: () => Promise<void>;
	clearError: () => void;
	resetOtpState: () => void;
	clearSession: () => void;
	isAuthenticated: () => boolean;
	hasStoredSession: () => boolean;
}

export const useAdminAuthStore = create<AdminAuthState>()(
	persist(
		(set, get) => ({
			operator: null,
			isLoading: false,
			isBootstrapping: false,
			error: null,
			otpSent: false,
			pendingPhone: null,
			sendOtp: async (phone) => {
				set({ isLoading: true, error: null });
				try {
					await adminAuthService.sendOtp(phone);
					set({
						isLoading: false,
						otpSent: true,
						pendingPhone: phone,
					});
				} catch (error) {
					const message =
						(error as { response?: { data?: { message?: string } } }).response
							?.data?.message || "Failed to send OTP";
					set({ isLoading: false, error: message });
					throw error;
				}
			},
			verifyOtp: async (phone, otp) => {
				set({ isLoading: true, error: null });
				try {
					await adminAuthService.verifyOtp(phone, otp);
					const operator = await adminAuthService.getAdminMe();
					set({
						operator,
						isLoading: false,
						otpSent: false,
						pendingPhone: null,
					});
				} catch (error) {
					const message =
						(error as { response?: { data?: { message?: string } } }).response
							?.data?.message || "Admin access denied";
					set({ isLoading: false, error: message });
					throw error;
				}
			},
			bootstrap: async () => {
				if (!getRefreshToken()) {
					set({ operator: null, isBootstrapping: false });
					return;
				}

				set({ isBootstrapping: true, error: null });
				try {
					const operator = await adminAuthService.getAdminMe();
					set({ operator, isBootstrapping: false });
				} catch (_error) {
					clearTokens();
					set({
						operator: null,
						isBootstrapping: false,
						otpSent: false,
						pendingPhone: null,
					});
				}
			},
			logout: async () => {
				await adminAuthService.logout();
				set({
					operator: null,
					otpSent: false,
					pendingPhone: null,
					error: null,
				});
			},
			clearError: () => set({ error: null }),
			resetOtpState: () =>
				set({ otpSent: false, pendingPhone: null, error: null }),
			clearSession: () => {
				clearTokens();
				set({
					operator: null,
					otpSent: false,
					pendingPhone: null,
					error: null,
					isBootstrapping: false,
				});
			},
			isAuthenticated: () => !!get().operator,
			hasStoredSession: () => !!getRefreshToken(),
		}),
		{
			name: "finishd-admin-auth",
			// v2 — bootstrap shape changed from { authorization: { mode } } to
			// { authorization: { permissions: string[] } }. Drop pre-v2 operators
			// so we re-bootstrap from /admin/me on next load.
			version: 2,
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				operator: state.operator,
			}),
			migrate: (persisted, fromVersion) => {
				if (fromVersion < 2) {
					return { operator: null };
				}
				return persisted as Partial<AdminAuthState>;
			},
		},
	),
);
